import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

// Services
import * as partTechnicianRequirementService from '../services/partTechnicianRequirementService';

const PartTechnicianRequirementForm = ({ partId, requirement, onRequirementChange, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const requirementId = requirement?.id;
  
  // Initial form values
  const initialValues = {
    part_id: partId || '',
    quantity_per_technician: requirement?.quantity_per_technician || 1
  };
  
  // Form validation schema
  const validationSchema = Yup.object().shape({
    quantity_per_technician: Yup.number()
      .required('Quantity per technician is required')
      .min(0, 'Quantity per technician must be a non-negative number')
      .integer('Quantity per technician must be a whole number')
  });
  
  // Handle form submission
  const handleSubmit = async (values, { setSubmitting }) => {
    setLoading(true);
    setError(null);
    
    try {
      let result;
      
      if (requirementId) {
        // Update existing requirement
        result = await partTechnicianRequirementService.updatePartTechnicianRequirement(
          requirementId,
          { quantity_per_technician: values.quantity_per_technician }
        );
        toast.success('Technician requirement updated successfully');
      } else {
        // Create new requirement
        result = await partTechnicianRequirementService.createPartTechnicianRequirement({
          part_id: partId,
          quantity_per_technician: values.quantity_per_technician
        });
        toast.success('Technician requirement created successfully');
      }
      
      // Notify parent component
      if (onRequirementChange) {
        onRequirementChange(result);
      }
      
      // Close the form
      onClose();
    } catch (err) {
      console.error('Error saving technician requirement:', err);
      setError(err.message || 'Failed to save technician requirement');
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {requirementId ? 'Edit Technician Requirement' : 'Add Technician Requirement'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}
        
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting
          }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    name="quantity_per_technician"
                    label="Quantity Per Technician"
                    value={values.quantity_per_technician}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.quantity_per_technician && Boolean(errors.quantity_per_technician)}
                    helperText={
                      touched.quantity_per_technician && errors.quantity_per_technician
                        ? errors.quantity_per_technician
                        : "Number of units required per technician"
                    }
                    InputProps={{ inputProps: { min: 0 } }}
                  />
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onClose}
                  sx={{ mr: 1 }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || loading}
                >
                  {loading ? <CircularProgress size={24} /> : 'Save'}
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default PartTechnicianRequirementForm;
